// The ads (docs/decisions.md, "Ads from the launch"): one slot on the tool, guide, FAQ and about pages (never on the
// legal, account, purchase or set-up pages: build/pages.ts), 728×90 where the slot is at least 728 px wide, 320×50
// below that. What fills it, in this order, and nothing reaches an ad network or Plugin Boutique before every earlier
// step allowed it:
// 1. the launch: ads.enabled in site.config.json (the page then has a slot) and launch.at has come;
// 2. Remove Ads: a visitor whose period is in force sees no ad, no disclosure and no reserved space (the head script
//    hides the slot before paint for a remembered period; entitlements.ts confirms it, and says when it ends);
// 3. consent: a bar at the bottom asks every visitor (identical Accept and Decline buttons, details, the policy link)
//    and the slot waits, empty, until the answer. Accept shows a Plugin Boutique affiliate banner (an image and a link
//    exactly as the affiliate panel supplies them, with the disclosure beside it); Decline shows A-ADS's banner, a plain
//    frame (no script and no cookie when checked, 2026-11-04). The browser's Global Privacy Control counts as Decline
//    until the visitor answers. The answer is kept in this browser with its time and the notice's version, holds six
//    months, and can be changed any time in "Cookie settings" (the footer, and the privacy policy).
// The notice changed with the 2026-11-04 amendment (version 2): an earlier acceptance was of Adsterra and is asked
// again; an earlier refusal stays a refusal for the rest of its six months. A period of Remove Ads starting or ending
// changes only what step 2 says: the answer stays as it was.
import { lang, translate, type MessageKey } from '../i18n';
import { adsRunning, launchAt, siteConfig, type AffiliateCreative } from '../site/config';

/** Where A-ADS's banner frames come from; the test builds point it at the preview server (.env.e2e). */
const aadsFrames = import.meta.env.VITE_AADS_FRAMES || 'https://acceptable.a-ads.com/';
/** A-ADS's units: 728×90 and 320×50. */
const AADS_UNITS: Record<number, string> = { 728: '2460347', 320: '2460348' };

/** The visitor's answer, as JSON: `{ choice, at, version }`. Plain strings from before 2026-10-29 are no answer. */
export const AD_COOKIES_KEY = 'chordToMidi.adCookies';
/**
 * Bumped when the notice changes (another ad company, new purposes), so every visitor is asked again: 2 since Plugin
 * Boutique replaced Adsterra (2026-11-04). A refusal under an earlier version stays a refusal until it would have expired.
 */
export const AD_CONSENT_VERSION = 2;
/** How long an answer holds before the visitor is asked again (six months, CNIL's figure). */
export const ANSWER_LIFETIME_MS = 183 * 86_400_000;

type Choice = 'accepted' | 'declined';
interface Answer {
  choice: Choice;
  at: string;
  version: number;
}

declare global {
  interface Navigator {
    globalPrivacyControl?: boolean;
  }
}

/**
 * The stored answer while it holds: this notice's version, younger than six months; an earlier version's refusal for
 * the rest of its six months (never renewed); anything else, an earlier acceptance above all, is no answer.
 */
export function storedAnswer(now: number = Date.now()): Answer | null {
  try {
    const raw = localStorage.getItem(AD_COOKIES_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<Answer> | null;
    if (!parsed || typeof parsed !== 'object') return null;
    const choice = parsed.choice === 'accepted' || parsed.choice === 'declined' ? parsed.choice : null;
    if (!choice || typeof parsed.version !== 'number' || typeof parsed.at !== 'string') return null;
    const at = Date.parse(parsed.at);
    if (Number.isNaN(at) || now - at > ANSWER_LIFETIME_MS || at - now > 86_400_000) return null;
    if (parsed.version === AD_CONSENT_VERSION) return { choice, at: parsed.at, version: parsed.version };
    // An answer to an earlier notice: only a refusal still counts, as it was given.
    if (parsed.version < AD_CONSENT_VERSION && choice === 'declined') return { choice, at: parsed.at, version: parsed.version };
    return null;
  } catch {
    return null;
  }
}

function remember(choice: Choice): void {
  const answer: Answer = { choice, at: new Date().toISOString(), version: AD_CONSENT_VERSION };
  try {
    localStorage.setItem(AD_COOKIES_KEY, JSON.stringify(answer));
  } catch {
    // Not remembered: the notice comes back on the next page.
  }
}

/** The browser's Global Privacy Control: "don't sell or share", which counts as Decline until the visitor answers. */
const gpcOn = () => navigator.globalPrivacyControl === true;

/** What applies now: the stored answer, else the GPC signal as a refusal, else nothing. */
export function effectiveChoice(now: number = Date.now()): Choice | null {
  return storedAnswer(now)?.choice ?? (gpcOn() ? 'declined' : null);
}

const t = (key: MessageKey) => translate(lang, key);

/** The Plugin Boutique banners, as site.config.json has them (a test build's stand-ins: src/site/preview.ts). */
function affiliateCreatives(): (AffiliateCreative & { image: string; href: string })[] {
  return (siteConfig.ads.affiliate?.creatives ?? []).filter(
    (creative): creative is AffiliateCreative & { image: string; href: string } =>
      typeof creative.image === 'string' && typeof creative.href === 'string',
  );
}

/** Plugin Boutique's banner: the image and the link as the panel gives them, and the disclosure beside it. */
function affiliateBanner(container: HTMLElement): HTMLElement | null {
  const creatives = affiliateCreatives().sort((a, b) => b.width - a.width);
  // The widest banner that fits; on a phone narrower than 352 px the slot reaches into the page's margins (site.css).
  const creative = creatives.find(({ width }) => width <= container.clientWidth) ?? creatives.at(-1);
  if (!creative) return null;
  const box = document.createElement('div');
  box.className = 'ad-affiliate';
  const link = document.createElement('a');
  link.className = 'ad-affiliate__link';
  link.href = creative.href;
  link.target = '_blank';
  link.rel = 'sponsored noopener';
  const image = document.createElement('img');
  image.src = creative.image;
  image.width = creative.width;
  image.height = creative.height;
  image.alt = creative.alt[lang];
  image.decoding = 'async';
  link.append(image);
  const disclosure = document.createElement('p');
  disclosure.className = 'ad-affiliate__disclosure';
  disclosure.textContent = t('site.affiliate.disclosure');
  box.append(link, disclosure);
  return box;
}

function aadsBanner(container: HTMLElement): HTMLElement {
  const width = container.clientWidth >= 728 ? 728 : 320;
  const height = width === 728 ? 90 : 50;
  const unit = AADS_UNITS[width]!;
  const frame = document.createElement('iframe');
  frame.dataset.aa = unit;
  frame.src = `${aadsFrames}${unit}/?size=Adaptive`;
  frame.width = String(width);
  frame.height = String(height);
  frame.title = container.getAttribute('aria-label') ?? '';
  return frame;
}

function policyLink(): HTMLAnchorElement {
  const link = document.createElement('a');
  link.href = t('site.adNotice.privacyHref');
  link.textContent = t('site.adNotice.privacy');
  return link;
}

/** The two answers, as identical buttons: refusing is as easy as accepting. */
function choiceButtons(onChoice: (choice: Choice) => void): HTMLElement {
  const actions = document.createElement('div');
  actions.className = 'ad-notice__actions';
  const choices: [MessageKey, Choice][] = [
    ['site.adNotice.accept', 'accepted'],
    ['site.adNotice.decline', 'declined'],
  ];
  for (const [key, choice] of choices) {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'ad-notice__choice';
    button.dataset.choice = choice;
    button.textContent = t(key);
    button.addEventListener('click', () => onChoice(choice));
    actions.append(button);
  }
  return actions;
}

/** Who, purposes, how long, how to change: the second layer, folded into the bar. */
function detailsPanel(): HTMLElement {
  const panel = document.createElement('div');
  panel.className = 'ad-notice__details';
  panel.hidden = true;
  const items: [MessageKey, MessageKey][] = [
    ['site.adNotice.who', 'site.adNotice.whoText'],
    ['site.adNotice.purposes', 'site.adNotice.purposesText'],
    ['site.adNotice.howLong', 'site.adNotice.howLongText'],
    ['site.adNotice.change', 'site.adNotice.changeText'],
  ];
  for (const [heading, body] of items) {
    const section = document.createElement('div');
    const title = document.createElement('h3');
    title.textContent = t(heading);
    const text = document.createElement('p');
    text.textContent = t(body);
    section.append(title, text);
    panel.append(section);
  }
  return panel;
}

function statusText(): string {
  const answer = storedAnswer();
  if (!answer) return t(gpcOn() ? 'site.adNotice.statusGpc' : 'site.adNotice.statusNone');
  const date = new Date(answer.at).toLocaleDateString(lang === 'ko' ? 'ko-KR' : 'en-GB', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
  return translate(lang, answer.choice === 'accepted' ? 'site.adNotice.statusAccepted' : 'site.adNotice.statusDeclined', {
    date,
  });
}

/** The page's ads: what fills its slots, and the consent bar and "Cookie settings" that decide it. */
export interface AdController {
  /** Remove Ads started (true: no ad, no disclosure, no space) or ended (false: the consent decides again). */
  setAdFree(adFree: boolean): void;
}

/**
 * Starts the page's ads: wires every "Cookie settings" button (the footer's, the privacy policy's) once, and fills the
 * slot as the launch, Remove Ads (`isAdFree`, which may wait briefly for a check) and the visitor's answer decide.
 */
export function startAds(isAdFree: () => boolean | Promise<boolean>): AdController {
  const containers = [...document.querySelectorAll<HTMLElement>('[data-ad-slot]')];
  let adFree = document.documentElement.classList.contains('ad-free');
  /** What the slots show now, so a repeated decision changes nothing. */
  let shown: Choice | 'none' | null = null;
  let launchTimer: ReturnType<typeof setTimeout> | undefined;

  const clear = () => {
    for (const container of containers) {
      container.replaceChildren();
      container.classList.remove('ad-slot--filled', 'ad-slot--affiliate');
    }
    shown = null;
  };

  const fill = (choice: Choice) => {
    if (shown === choice) return;
    clear();
    for (const container of containers) {
      container.hidden = false;
      const banner = choice === 'accepted' ? affiliateBanner(container) : aadsBanner(container);
      if (!banner) {
        // No banner to show (none configured): the slot folds rather than keep an empty space.
        container.hidden = true;
        continue;
      }
      container.classList.add('ad-slot--filled');
      if (choice === 'accepted') container.classList.add('ad-slot--affiliate');
      container.append(banner);
    }
    shown = choice;
  };

  const removeBar = () => document.querySelector('.ad-notice')?.remove();

  const showBar = () => {
    if (document.querySelector('.ad-notice')) return;
    const bar = document.createElement('div');
    bar.className = 'ad-notice';
    bar.setAttribute('role', 'region');
    bar.setAttribute('aria-label', t('site.adNotice.label'));
    const details = detailsPanel();
    const text = document.createElement('p');
    text.className = 'ad-notice__text';
    text.append(`${t('site.adNotice.ask')} `, policyLink());
    const toggle = document.createElement('button');
    toggle.type = 'button';
    toggle.className = 'ad-notice__toggle';
    toggle.setAttribute('aria-expanded', 'false');
    toggle.textContent = t('site.adNotice.details');
    toggle.addEventListener('click', () => {
      const open = details.hidden;
      details.hidden = !open;
      toggle.setAttribute('aria-expanded', String(open));
      toggle.textContent = t(open ? 'site.adNotice.hideDetails' : 'site.adNotice.details');
    });
    const actions = choiceButtons((choice) => {
      remember(choice);
      decide();
    });
    actions.prepend(toggle);
    bar.append(details, text, actions);
    document.body.append(bar);
  };

  /** Steps 1–3, from the start: called whenever the launch, Remove Ads or the answer may have changed. */
  const decide = () => {
    if (containers.length === 0) return;
    const now = Date.now();
    if (!adsRunning(now)) {
      // Before the launch: the slot folds, nothing loads; a page left open until then decides again at it.
      clear();
      removeBar();
      for (const container of containers) container.hidden = true;
      if (siteConfig.ads.enabled && launchAt > now && launchTimer === undefined) {
        launchTimer = setTimeout(
          () => {
            launchTimer = undefined;
            decide();
          },
          Math.min(launchAt - now, 6 * 3_600_000) + 1,
        );
      }
      return;
    }
    if (adFree) {
      document.documentElement.classList.add('ad-free');
      clear();
      removeBar();
      return;
    }
    document.documentElement.classList.remove('ad-free');
    const choice = effectiveChoice(now);
    if (choice === null) {
      // The slot keeps its space, empty, while the visitor decides.
      clear();
      for (const container of containers) container.hidden = false;
      shown = 'none';
      showBar();
      return;
    }
    removeBar();
    fill(choice);
  };

  const openSettings = () => {
    document.querySelector('.ad-settings')?.remove();
    const dialog = document.createElement('dialog');
    dialog.className = 'ad-settings';
    dialog.setAttribute('aria-labelledby', 'ad-settings-title');
    const title = document.createElement('h2');
    title.id = 'ad-settings-title';
    title.textContent = t('site.adNotice.settingsTitle');
    const status = document.createElement('p');
    status.className = 'ad-settings__status';
    status.textContent = statusText();
    const text = document.createElement('p');
    text.textContent = t('site.adNotice.ask');
    const actions = choiceButtons((choice) => {
      remember(choice);
      dialog.close();
      decide();
    });
    const links = document.createElement('div');
    links.className = 'ad-settings__links';
    const close = document.createElement('button');
    close.type = 'button';
    close.className = 'ad-settings__close';
    close.textContent = t('site.adNotice.close');
    close.addEventListener('click', () => dialog.close());
    links.append(policyLink(), close);
    dialog.append(title, status, text, actions, links);
    dialog.addEventListener('click', (event) => {
      if (event.target === dialog) dialog.close();
    });
    dialog.addEventListener('close', () => dialog.remove());
    document.body.append(dialog);
    dialog.showModal();
  };
  for (const button of document.querySelectorAll<HTMLButtonElement>('[data-ad-settings]')) {
    button.addEventListener('click', openSettings);
  }

  // A visitor remembered as having Remove Ads may wait a moment for its check; anyone else decides at once.
  let checked = false;
  const first = isAdFree();
  if (typeof first === 'boolean') {
    adFree = adFree || first;
    checked = true;
    decide();
  } else {
    // Until the check answers, the slot stays as the head script left it (hidden for a remembered period).
    void first.then(
      (answer) => {
        if (checked) return;
        checked = true;
        adFree = answer;
        decide();
      },
      () => {
        if (checked) return;
        checked = true;
        decide();
      },
    );
  }

  return {
    setAdFree(next: boolean) {
      checked = true;
      if (next === adFree && shown !== null) return;
      adFree = next;
      decide();
    },
  };
}
